import ChatbotAPI from './ChatbotAPI';

class FlowExecutionEngine {
  constructor() {
    this.currentFlow = null;
    this.currentNode = null;
    this.conversationState = {};
    this.conversationId = null;
    this.customerData = {};
  }

  // Initialize a new conversation with a flow
  async initializeConversation(flowId, customerData) {
    try {
      // Load the flow from backend
      this.currentFlow = await ChatbotAPI.getFlow(flowId);
      if (!this.currentFlow) {
        throw new Error('Failed to load flow');
      }

      // Start conversation with backend
      const conversation = await ChatbotAPI.startConversation(flowId, customerData);
      if (!conversation) {
        throw new Error('Failed to start conversation');
      }

      this.conversationId = conversation.id;
      this.customerData = customerData;
      this.conversationState = {
        variables: {},
        context: {},
        history: [],
        currentNodeId: null
      };

      // Find the starting node (node with no incoming edges)
      const startNode = this.findStartNode();
      if (startNode) {
        this.currentNode = startNode;
        this.conversationState.currentNodeId = startNode.id;
        
        // Execute the start node
        return await this.executeNode(startNode);
      }

      throw new Error('No start node found in flow');
    } catch (error) {
      console.error('Error initializing conversation:', error);
      throw error;
    }
  }

  // Initialize a new conversation with chatbot data directly
  async initializeChatbotConversation(chatbotData, customerData) {
    try {
      this.currentFlow = chatbotData;
      this.customerData = customerData;
      this.conversationState = {
        variables: {},
        context: {},
        history: [],
        currentNodeId: null,
        currentKeyword: null,
        currentNodeIndex: 0
      };

      // Start conversation with backend if needed
      if (chatbotData.id) {
        const conversation = await ChatbotAPI.startConversation(chatbotData.id, customerData);
        if (conversation) {
          this.conversationId = conversation.id;
        }
      }

      // Return initial state - waiting for user input
      return {
        type: 'waiting_for_input',
        message: 'Please type a message to start the conversation.',
        triggerKeywords: chatbotData.tgm || []
      };
    } catch (error) {
      console.error('Error initializing chatbot conversation:', error);
      throw error;
    }
  }

  // Find the starting node (node with no incoming edges)
  findStartNode() {
    if (!this.currentFlow || !this.currentFlow.nodes) {
      return null;
    }

    const nodes = this.currentFlow.nodes;
    const edges = this.currentFlow.edges || [];

    // Find nodes that have no incoming edges
    const startNodes = nodes.filter(node => {
      return !edges.some(edge => edge.target === node.id);
    });

    // If multiple start nodes, use the first one
    return startNodes.length > 0 ? startNodes[0] : null;
  }

  // Execute a specific node
  async executeNode(node) {
    if (!node) {
      throw new Error('No node to execute');
    }

    this.currentNode = node;
    this.conversationState.currentNodeId = node.id;

    const nodeType = node.type || node.data?.type;
    const nodeData = node.data || {};

    switch (nodeType) {
      case 'message':
        return await this.executeMessageNode(nodeData);
      
      case 'condition':
        return await this.executeConditionNode(nodeData);
      
      case 'action':
        return await this.executeActionNode(nodeData);
      
      case 'input':
        return await this.executeInputNode(nodeData);
      
      case 'template':
        return await this.executeTemplateNode(nodeData);
      
      case 'media':
        return await this.executeMediaNode(nodeData);
      
      case 'list':
        return await this.executeListNode(nodeData);
      
      case 'product':
        return await this.executeProductNode(nodeData);
      
      case 'intervention':
        return await this.executeInterventionNode(nodeData);
      
      default:
        console.warn(`Unknown node type: ${nodeType}`);
        return this.getNextNode(node);
    }
  }

  // Execute a message node
  async executeMessageNode(nodeData) {
    const message = this.processVariables(nodeData.message || '');
    
    // Send message to customer
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, message, this.currentNode.id);
    }

    // Add to conversation history
    this.conversationState.history.push({
      type: 'bot_message',
      content: message,
      nodeId: this.currentNode.id,
      timestamp: new Date().toISOString()
    });

    return {
      type: 'message',
      content: message,
      buttons: nodeData.buttons || [],
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Execute a condition node
  async executeConditionNode(nodeData) {
    const condition = nodeData.condition || '';
    const result = this.evaluateCondition(condition);
    
    // Find the appropriate edge based on condition result
    const edges = this.currentFlow.edges || [];
    const outgoingEdges = edges.filter(edge => edge.source === this.currentNode.id);
    
    let nextNodeId = null;
    
    if (result && outgoingEdges.length > 0) {
      // Condition is true, take the first edge
      nextNodeId = outgoingEdges[0].target;
    } else if (!result && outgoingEdges.length > 1) {
      // Condition is false, take the second edge (if exists)
      nextNodeId = outgoingEdges[1].target;
    }

    if (nextNodeId) {
      const nextNode = this.currentFlow.nodes.find(node => node.id === nextNodeId);
      return await this.executeNode(nextNode);
    }

    return { type: 'end', message: 'Conversation ended' };
  }

  // Execute an action node
  async executeActionNode(nodeData) {
    const action = nodeData.action || '';
    
    switch (action) {
      case 'set_variable':
        this.conversationState.variables[nodeData.variableName] = nodeData.variableValue;
        break;
      
      case 'send_webhook':
        await this.sendWebhook(nodeData.webhookUrl, nodeData.webhookData);
        break;
      
      case 'save_to_database':
        await this.saveToDatabase(nodeData.tableName, nodeData.data);
        break;
      
      case 'send_email':
        await this.sendEmail(nodeData.emailData);
        break;
      
      default:
        console.warn(`Unknown action: ${action}`);
    }

    return this.getNextNode(this.currentNode);
  }

  // Execute an input node
  async executeInputNode(nodeData) {
    return {
      type: 'input_required',
      prompt: nodeData.prompt || 'Please provide your input:',
      validation: nodeData.validation || {},
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Execute a template node
  async executeTemplateNode(nodeData) {
    const templateName = nodeData.templateName;
    const templateParams = nodeData.templateParams || {};
    
    // Process template parameters with variables
    const processedParams = {};
    Object.keys(templateParams).forEach(key => {
      processedParams[key] = this.processVariables(templateParams[key]);
    });

    // Send template message
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, `Template: ${templateName}`, this.currentNode.id);
    }

    return {
      type: 'template_sent',
      templateName: templateName,
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Execute a media node
  async executeMediaNode(nodeData) {
    const message = this.processVariables(nodeData.message || '');
    const mediaUrl = nodeData.mediaUrl;
    const mediaType = nodeData.mediaType || 'image';
    
    // Send media message
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, message, this.currentNode.id);
    }

    return {
      type: 'media',
      content: message,
      mediaUrl: mediaUrl,
      mediaType: mediaType,
      buttons: nodeData.buttons || [],
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Execute a list node
  async executeListNode(nodeData) {
    const message = this.processVariables(nodeData.message || '');
    const listItems = nodeData.listItems || [];
    
    // Send list message
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, message, this.currentNode.id);
    }

    return {
      type: 'list',
      content: message,
      listItems: listItems,
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Execute a product node
  async executeProductNode(nodeData) {
    const message = this.processVariables(nodeData.message || '');
    const product = nodeData.product || {};
    
    // Send product message
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, message, this.currentNode.id);
    }

    return {
      type: 'product',
      content: message,
      product: product,
      buttons: nodeData.buttons || [],
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Execute an intervention node
  async executeInterventionNode(nodeData) {
    const message = this.processVariables(nodeData.message || '');
    
    // Send intervention request
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, message, this.currentNode.id);
    }

    return {
      type: 'intervention_requested',
      content: message,
      nextNode: this.getNextNode(this.currentNode)
    };
  }

  // Process user input and determine next step
  async processUserInput(userInput) {
    if (!this.currentNode) {
      throw new Error('No active conversation');
    }

    // Add user input to history
    this.conversationState.history.push({
      type: 'user_input',
      content: userInput,
      timestamp: new Date().toISOString()
    });

    // Send user input to backend
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, userInput);
    }

    // Check if current node expects input
    const nodeType = this.currentNode.type || this.currentNode.data?.type;
    
    if (nodeType === 'input') {
      // Validate input if validation rules exist
      const validation = this.currentNode.data?.validation || {};
      const isValid = this.validateInput(userInput, validation);
      
      if (!isValid) {
        return {
          type: 'validation_error',
          message: validation.errorMessage || 'Invalid input. Please try again.',
          currentNode: this.currentNode
        };
      }

      // Store input in variables if specified
      const variableName = this.currentNode.data?.variableName;
      if (variableName) {
        this.conversationState.variables[variableName] = userInput;
      }
    }

    // Find next node based on user input
    const nextNode = this.findNextNodeByInput(userInput);
    
    if (nextNode) {
      return await this.executeNode(nextNode);
    } else {
      // No matching next node, end conversation
      return {
        type: 'end',
        message: 'Thank you for your input. Conversation ended.'
      };
    }
  }

  // Process user input for chatbot data
  async processChatbotUserInput(userInput) {
    if (!this.currentFlow) {
      throw new Error('No active chatbot conversation');
    }

    // Add user input to history
    this.conversationState.history.push({
      type: 'user_input',
      content: userInput,
      timestamp: new Date().toISOString()
    });

    // Send user input to backend if conversation exists
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, userInput);
    }

    const chatbotNodes = this.currentFlow.chatbot_node || {};
    const triggerKeywords = this.currentFlow.tgm || [];

    // Check if this is a new conversation start
    if (!this.conversationState.currentKeyword) {
      // Check if user input matches any trigger keyword
      const matchedKeyword = this.findMatchingKeyword(userInput, triggerKeywords);
      
      if (matchedKeyword && chatbotNodes[matchedKeyword]) {
        this.conversationState.currentKeyword = matchedKeyword;
        this.conversationState.currentNodeIndex = 0;
        
        // Execute the first node in the sequence
        return await this.executeChatbotNode(chatbotNodes[matchedKeyword][0], matchedKeyword, 0);
      } else {
        // No matching trigger keyword
        return {
          type: 'no_match',
          message: 'I\'m sorry, I didn\'t understand that. Please try one of these keywords: ' + triggerKeywords.join(', '),
          triggerKeywords: triggerKeywords
        };
      }
    } else {
      // Continue with existing conversation flow
      const currentNodeArray = chatbotNodes[this.conversationState.currentKeyword];
      
      if (!currentNodeArray || this.conversationState.currentNodeIndex >= currentNodeArray.length) {
        // End of conversation for this keyword
        this.conversationState.currentKeyword = null;
        this.conversationState.currentNodeIndex = 0;
        
        return {
          type: 'end',
          message: 'Thank you for your conversation. You can start a new conversation anytime!',
          triggerKeywords: triggerKeywords
        };
      }

      // Check if current node expects user input (like buttons)
      const currentNode = currentNodeArray[this.conversationState.currentNodeIndex];
      
      if (currentNode.type === 'interactive' || currentNode.type === 'buttons') {
        // Check if user input matches any button
        const buttons = currentNode.buttons || [];
        const matchedButton = buttons.find(button => 
          button.label && userInput.toLowerCase().includes(button.label.toLowerCase())
        );
        
        if (matchedButton) {
          // Move to next node
          this.conversationState.currentNodeIndex++;
          
          if (this.conversationState.currentNodeIndex < currentNodeArray.length) {
            return await this.executeChatbotNode(currentNodeArray[this.conversationState.currentNodeIndex], this.conversationState.currentKeyword, this.conversationState.currentNodeIndex);
          } else {
            // End of conversation
            this.conversationState.currentKeyword = null;
            this.conversationState.currentNodeIndex = 0;
            
            return {
              type: 'end',
              message: 'Thank you for your conversation. You can start a new conversation anytime!',
              triggerKeywords: triggerKeywords
            };
          }
        } else {
          // Invalid button selection
          return {
            type: 'invalid_input',
            message: 'Please select a valid option: ' + buttons.map(b => b.label).join(', '),
            buttons: buttons
          };
        }
      } else {
        // Regular message node, move to next
        this.conversationState.currentNodeIndex++;
        
        if (this.conversationState.currentNodeIndex < currentNodeArray.length) {
          return await this.executeChatbotNode(currentNodeArray[this.conversationState.currentNodeIndex], this.conversationState.currentKeyword, this.conversationState.currentNodeIndex);
        } else {
          // End of conversation
          this.conversationState.currentKeyword = null;
          this.conversationState.currentNodeIndex = 0;
          
          return {
            type: 'end',
            message: 'Thank you for your conversation. You can start a new conversation anytime!',
            triggerKeywords: triggerKeywords
          };
        }
      }
    }
  }

  // Execute a single chatbot node
  async executeChatbotNode(nodeConfig, keyword, index) {
    if (!nodeConfig) {
      throw new Error('No node configuration provided');
    }

    // Send message to backend if conversation exists
    if (this.conversationId) {
      await ChatbotAPI.sendMessage(this.conversationId, nodeConfig.message || '', nodeConfig.node_id);
    }

    // Add to history
    this.conversationState.history.push({
      type: 'bot_response',
      content: nodeConfig.message || '',
      nodeId: nodeConfig.node_id,
      timestamp: new Date().toISOString()
    });

    // Apply delay if specified
    if (nodeConfig.delay && nodeConfig.delay > 0) {
      await new Promise(resolve => setTimeout(resolve, nodeConfig.delay * 1000));
    }

    // Return response based on node type
    switch (nodeConfig.type) {
      case 'message':
        return {
          type: 'message',
          content: nodeConfig.message || '',
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'interactive':
      case 'buttons':
        return {
          type: 'interactive',
          content: nodeConfig.message || '',
          buttons: nodeConfig.buttons || [],
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'media':
        return {
          type: 'media',
          content: nodeConfig.message || '',
          mediaUrl: nodeConfig.media_url || '',
          mediaType: nodeConfig.media_type || 'image',
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'audio':
        return {
          type: 'audio',
          content: nodeConfig.message || '',
          audioUrl: nodeConfig.audio_url || '',
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'file':
        return {
          type: 'file',
          content: nodeConfig.message || '',
          fileUrl: nodeConfig.file_url || '',
          fileName: nodeConfig.file_name || '',
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'cta':
        return {
          type: 'cta',
          content: nodeConfig.message || '',
          url: nodeConfig.url || '',
          buttonText: nodeConfig.button_text || 'Click Here',
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'ecommerce':
        return {
          type: 'ecommerce',
          content: nodeConfig.message || '',
          products: nodeConfig.products || [],
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'list':
        return {
          type: 'list',
          content: nodeConfig.message || '',
          listItems: nodeConfig.list_items || [],
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'api':
        return {
          type: 'api',
          content: nodeConfig.message || '',
          apiUrl: nodeConfig.api_url || '',
          method: nodeConfig.method || 'GET',
          nextNodeId: nodeConfig.next_node_id
        };
        
      case 'condition':
        return {
          type: 'condition',
          condition: nodeConfig.condition || '',
          trueNodeId: nodeConfig.true_node_id || null,
          falseNodeId: nodeConfig.false_node_id || null,
          nextNodeId: nodeConfig.next_node_id
        };
        
      default:
        return {
          type: 'message',
          content: nodeConfig.message || '',
          nextNodeId: nodeConfig.next_node_id
        };
    }
  }

  // Find matching keyword from user input
  findMatchingKeyword(userInput, triggerKeywords) {
    const input = userInput.toLowerCase().trim();
    
    for (const keyword of triggerKeywords) {
      if (input.includes(keyword.toLowerCase())) {
        return keyword;
      }
    }
    
    return null;
  }

  // Find next node based on user input
  findNextNodeByInput(userInput) {
    const edges = this.currentFlow.edges || [];
    const outgoingEdges = edges.filter(edge => edge.source === this.currentNode.id);
    
    // Check if any edge has a condition that matches the input
    for (const edge of outgoingEdges) {
      const condition = edge.data?.condition;
      if (condition && this.matchesCondition(userInput, condition)) {
        const nextNode = this.currentFlow.nodes.find(node => node.id === edge.target);
        return nextNode;
      }
    }
    
    // If no condition matches, take the first edge (default path)
    if (outgoingEdges.length > 0) {
      const nextNode = this.currentFlow.nodes.find(node => node.id === outgoingEdges[0].target);
      return nextNode;
    }
    
    return null;
  }

  // Get the next node in the flow
  getNextNode(currentNode) {
    const edges = this.currentFlow.edges || [];
    const outgoingEdges = edges.filter(edge => edge.source === currentNode.id);
    
    if (outgoingEdges.length > 0) {
      const nextNode = this.currentFlow.nodes.find(node => node.id === outgoingEdges[0].target);
      return nextNode;
    }
    
    return null;
  }

  // Process variables in text (replace {{variable}} with actual values)
  processVariables(text) {
    if (!text) return text;
    
    return text.replace(/\{\{(\w+)\}\}/g, (match, variableName) => {
      return this.conversationState.variables[variableName] || match;
    });
  }

  // Evaluate a condition
  evaluateCondition(condition) {
    try {
      // Simple condition evaluation - can be extended for complex logic
      if (!condition) return true;
      
      // Replace variables in condition
      const processedCondition = this.processVariables(condition);
      
      // For now, just return true - can be extended with proper expression evaluation
      return true;
    } catch (error) {
      console.error('Error evaluating condition:', error);
      return false;
    }
  }

  // Check if user input matches a condition
  matchesCondition(userInput, condition) {
    if (!condition) return true;
    
    const input = userInput.toLowerCase();
    const conditionLower = condition.toLowerCase();
    
    // Simple keyword matching
    if (conditionLower.includes('contains:')) {
      const keyword = conditionLower.split('contains:')[1].trim();
      return input.includes(keyword);
    }
    
    // Exact match
    if (conditionLower.includes('equals:')) {
      const value = conditionLower.split('equals:')[1].trim();
      return input === value;
    }
    
    // Default: check if input contains the condition
    return input.includes(conditionLower);
  }

  // Validate user input
  validateInput(input, validation) {
    if (!validation) return true;
    
    if (validation.required && !input.trim()) {
      return false;
    }
    
    if (validation.minLength && input.length < validation.minLength) {
      return false;
    }
    
    if (validation.maxLength && input.length > validation.maxLength) {
      return false;
    }
    
    if (validation.pattern && !new RegExp(validation.pattern).test(input)) {
      return false;
    }
    
    return true;
  }

  // Send webhook
  async sendWebhook(url, data) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      return response.ok;
    } catch (error) {
      console.error('Webhook error:', error);
      return false;
    }
  }

  // Save to database (placeholder)
  async saveToDatabase(tableName, data) {
    // This would integrate with your backend database
    console.log('Saving to database:', tableName, data);
  }

  // Send email (placeholder)
  async sendEmail(emailData) {
    // This would integrate with your email service
    console.log('Sending email:', emailData);
  }

  // Get conversation state
  getConversationState() {
    return {
      ...this.conversationState,
      flowId: this.currentFlow?.id,
      conversationId: this.conversationId,
      customerData: this.customerData
    };
  }

  // End conversation
  async endConversation(reason = 'completed') {
    if (this.conversationId) {
      await ChatbotAPI.endConversation(this.conversationId, reason);
    }
    
    this.currentFlow = null;
    this.currentNode = null;
    this.conversationState = {};
    this.conversationId = null;
    this.customerData = {};
  }
}

export default FlowExecutionEngine; 